import { Injectable } from '@angular/core';
import { User } from '../models/user.model';

const USER_STORAGE_KEY = 'user';

/**
 * The single answer to "which teams are mine?" on the front, mirroring
 * utils/scope.js on the backend. Nothing else should read user.teamId directly.
 *
 * Only distributor accounts carry teams: that is how a distributor is sliced.
 * Head office and optical ones are bounded by their own entity subtree instead,
 * which the backend already resolves, so they answer null here.
 */
@Injectable({
  providedIn: 'root'
})
export class TeamScopeService {

  private cachedRaw: string | null = null;
  private cachedUser: User | null = null;

  /**
   * `null` means the account has no team and therefore sees its whole hierarchy,
   * exactly as before this feature. It is never an empty array: `[]` would read
   * as "nothing matches", which is the opposite.
   */
  myTeamIds(): number[] | null {
    const user = this.currentUser();
    if (!user) return null;

    const ids: number[] = [];

    // The principal always counts, even if the membership failed to travel:
    // before the migration runs it is the only team there is.
    const principalId = Number(user.teamId);
    if (principalId) ids.push(principalId);

    for (const team of user.teams ?? []) {
      const id = Number(team?.id);
      if (id && !ids.includes(id)) ids.push(id);
    }

    return ids.length ? ids : null;
  }

  hasMultipleTeams(): boolean {
    return (this.myTeamIds()?.length ?? 0) > 1;
  }

  /** The team a filter should be pinned to: only when there is exactly one. */
  fixedTeamId(): number | null {
    const ids = this.myTeamIds();
    return ids && ids.length === 1 ? ids[0] : null;
  }

  /** Whether something filed under `teamId` falls inside the account's scope. */
  isInScope(teamId: number | null | undefined): boolean {
    const ids = this.myTeamIds();
    if (!ids) return true;
    return teamId != null && ids.includes(Number(teamId));
  }

  /**
   * Parsed once per stored value: these are read from template getters, so
   * re-parsing the user on every change detection cycle would not be free.
   */
  private currentUser(): User | null {
    let raw: string | null = null;
    try {
      raw = localStorage.getItem(USER_STORAGE_KEY);
    } catch {
      return null;
    }

    if (raw === this.cachedRaw) return this.cachedUser;

    this.cachedRaw = raw;
    try {
      this.cachedUser = raw ? (JSON.parse(raw) as User) : null;
    } catch {
      this.cachedUser = null;
    }
    return this.cachedUser;
  }
}
